import { api } from './api';
import { PaginatedResponse } from './api';

export interface WebhookEvent {
  id: string;
  provider: 'telnyx';
  event_type: string;
  event_id: string;
  payload: Record<string, any>;
  received_at: string;
  processed_at?: string;
  status: 'pending' | 'processing' | 'processed' | 'failed';
  error_message?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface WebhookEventFilters {
  page?: number;
  limit?: number;
  provider?: string;
  eventType?: string;
  status?: 'pending' | 'processing' | 'processed' | 'failed';
  startDate?: string;
  endDate?: string;
}

export interface WebhookEventResponse {
  events: WebhookEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface WebhookStats {
  total: number;
  pending: number;
  processing: number;
  processed: number;
  failed: number;
  todayTotal: number;
  todayProcessed: number;
  todayFailed: number;
}

export const webhookService = {
  // Get webhook events with filtering and pagination
  async getWebhookEvents(filters?: WebhookEventFilters): Promise<WebhookEventResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/webhooks/events?${params.toString()}`);
    return response.data.data;
  },

  // Get a specific webhook event by ID
  async getWebhookEvent(id: string): Promise<WebhookEvent> {
    const response = await api.get(`/webhooks/events/${id}`);
    return response.data.data;
  },

  // Retry a failed webhook event
  async retryWebhookEvent(id: string): Promise<void> {
    await api.post(`/webhooks/events/${id}/retry`);
  },

  // Clean up old webhook events
  async cleanupOldEvents(olderThanDays: number = 30): Promise<{ deletedCount: number }> {
    const response = await api.delete(`/webhooks/events/cleanup?olderThanDays=${olderThanDays}`);
    return response.data.data;
  },

  async deleteAllEvents(): Promise<{ deletedCount: number }> {
    const response = await api.delete('/webhooks/events/all');
    return response.data.data;
  },

  // Send a test webhook (for development)
  async sendTestWebhook(data: {
    eventType: string;
    callId?: string;
    leadPhone?: string;
  }): Promise<{ eventId: string; webhookEventId: string; payload: any }> {
    const response = await api.post('/webhooks/telnyx/test', data);
    return response.data.data;
  },

  // Get webhook statistics
  async getWebhookStats(): Promise<WebhookStats> {
    // This would need to be implemented in the backend
    // For now, we'll calculate basic stats from the events
    const allEvents = await this.getWebhookEvents({ limit: 1000 });
    const events = allEvents.events;
    
    const today = new Date().toISOString().split('T')[0];
    const todayEvents = events.filter(event => 
      event.received_at.startsWith(today)
    );
    
    return {
      total: allEvents.pagination.total,
      pending: events.filter(e => e.status === 'pending').length,
      processing: events.filter(e => e.status === 'processing').length,
      processed: events.filter(e => e.status === 'processed').length,
      failed: events.filter(e => e.status === 'failed').length,
      todayTotal: todayEvents.length,
      todayProcessed: todayEvents.filter(e => e.status === 'processed').length,
      todayFailed: todayEvents.filter(e => e.status === 'failed').length,
    };
  },

  // Format event type for display
  formatEventType(eventType: string): string {
    return eventType
      .split('.')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  // Get status color for UI
  getStatusColor(status: WebhookEvent['status']): string {
    switch (status) {
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'processing':
        return 'text-primary bg-primary/10';
      case 'processed':
        return 'text-success bg-success/10';
      case 'failed':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  },

  // Format date for display
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  },

  // Get relative time (e.g., "2 minutes ago")
  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  },
};